import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiEdit, FiMenu, FiTrash } from 'react-icons/fi';
import debounce from 'lodash.debounce';
import { getAllSurveyHeaders } from '../apicalls/surveyHeaders';
import { getAllQuestions, updateQuestion } from '../apicalls/questions';
import { isAuthenticated } from '../apicalls/auth';
import DeletePopup from './PopupForm/DeletePopup';
import OptionGroups from './OptionGroups';
import Paginations from './Paginations';
import QuestionForm from './PopupForm/QuestionForm';
import SurveyHeaders from './SurveyHeaders';

function Questions() {
    const [questions, setQuestions] = useState([]);
    const [surveyHeaders, setSurveyHeaders] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [selectedSurveyHeaderId, setSelectedSurveyHeaderId] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [draggedQuestionId, setDraggedQuestionId] = useState(null);
    const [isReordering, setIsReordering] = useState(false);
    const [modalShow, setModalShow] = useState({ add: false, edit: false, delete: false });
    const user = isAuthenticated();

    useEffect(() => {
        const fetchSurveyHeaders = async () => {
            const headers = await getAllSurveyHeaders(user.access_token);
            setSurveyHeaders(headers || []);
        };

        fetchSurveyHeaders();
    }, [user.access_token]);

    useEffect(() => {
        const fetchData = async () => {
            const shouldLoadAllForSelectedHeader = Boolean(selectedSurveyHeaderId) && search === "";
            const response = await getAllQuestions(
                user.access_token,
                shouldLoadAllForSelectedHeader ? 1000 : perPage,
                currentPage,
                search,
                selectedSurveyHeaderId
            );

            setQuestions(sortQuestions(response.questions || []));
            setTotalQuestions(parseInt(response?.total ?? 0, 10) || 0);
        };

        fetchData();
    }, [currentPage, modalShow, perPage, search, selectedSurveyHeaderId, user.access_token]);

    const handleSearch = debounce((event) => {
        setSearch(event.target.value);
        setCurrentPage(1);
        setExpandedId(null);
    }, 500);

    const paginate = (number) => setCurrentPage(number);

    const handleSurveyHeaderFilter = (surveyHeaderId) => {
        setSelectedSurveyHeaderId(surveyHeaderId);
        setCurrentPage(1);
        setExpandedId(null);
    };

    const handlePerPageChange = (event) => {
        setPerPage(parseInt(event.target.value, 10));
        setCurrentPage(1);
    };

    const handleDeleteSuccess = () => {
        setQuestions((previousQuestions) =>
            previousQuestions.filter((question) => question.id !== currentQuestion.id)
        );
        setTotalQuestions((previousTotal) => previousTotal - 1);
        setCurrentQuestion(null);
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const canReorderQuestions = Boolean(selectedSurveyHeaderId) && search === "";

    const handleDragStart = (questionId) => {
        if (!canReorderQuestions || isReordering) {
            return;
        }

        setDraggedQuestionId(questionId);
    };

    const handleDragEnd = () => {
        setDraggedQuestionId(null);
    };

    const handleDrop = async (targetQuestion) => {
        if (!canReorderQuestions || isReordering || !draggedQuestionId || draggedQuestionId === targetQuestion.id) {
            return;
        }

        const draggedQuestion = questions.find((question) => question.id === draggedQuestionId);

        if (!draggedQuestion || draggedQuestion.survey_section_id !== targetQuestion.survey_section_id) {
            setDraggedQuestionId(null);
            return;
        }

        const reorderedQuestions = reorderQuestionsWithinSection(
            questions,
            draggedQuestionId,
            targetQuestion.id
        );
        const reorderedSectionQuestions = reorderedQuestions.filter(
            (question) => question.survey_section_id === targetQuestion.survey_section_id
        );

        setQuestions(reorderedQuestions);
        setIsReordering(true);

        try {
            await Promise.all(
                reorderedSectionQuestions.map((question, index) =>
                    updateQuestion(
                        question.id,
                        {
                            sort_order: index + 1,
                            survey_header_id: question.survey_header_id,
                            survey_section_id: question.survey_section_id,
                        },
                        user.access_token
                    )
                )
            );
        } catch (error) {
            console.error('Error updating question sort order:', error);
        } finally {
            setDraggedQuestionId(null);
            setIsReordering(false);
        }
    };

    return (
        <main className="container-fluid mt-3">
            <SurveyHeaders />
            <OptionGroups />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1>Questions</h1>
                <button
                    className="btn btn-primary"
                    onClick={() => setModalShow({ ...modalShow, add: true })}
                >
                    Add Question +
                </button>
            </div>
            {modalShow.add && (
                <QuestionForm
                    action="Add"
                    question={null}
                    setPopup={() => setModalShow({ ...modalShow, add: false })}
                />
            )}
            <div className="mb-3">
                <div className="d-flex align-items-center flex-wrap">
                    <span className="mr-2 mb-2"><strong>Filter by Survey Header:</strong></span>
                    <button
                        className={`btn mb-2 mr-2 ${selectedSurveyHeaderId === "" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => handleSurveyHeaderFilter("")}
                        type="button"
                    >
                        All Questions
                    </button>
                    {surveyHeaders.map((header) => (
                        <button
                            key={header.id}
                            className={`btn mb-2 mr-2 ${selectedSurveyHeaderId === header.id.toString() ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => handleSurveyHeaderFilter(header.id.toString())}
                            type="button"
                        >
                            {header.survey_name}
                        </button>
                    ))}
                </div>
                <small className="text-muted d-block mt-2">
                    {canReorderQuestions
                        ? 'Drag questions within a section to save sort order automatically.'
                        : 'Select a survey header and clear search to enable drag reordering.'}
                </small>
            </div>
            <div className="mb-3 d-flex align-items-center">
                <input
                    className="form-control w-25"
                    type="text"
                    onChange={handleSearch}
                    placeholder="Search"
                />
                <div className="ml-3">
                    <label htmlFor="perPage">Items per page: </label>
                    <select
                        id="perPage"
                        className="form-select ml-2"
                        value={perPage}
                        onChange={handlePerPageChange}
                        disabled={Boolean(selectedSurveyHeaderId) && search === ""}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                </div>
            </div>
            {renderQuestionSections({
                canReorderQuestions,
                draggedQuestionId,
                expandedId,
                handleDragEnd,
                handleDragStart,
                handleDrop,
                isReordering,
                modalShow,
                questions,
                setCurrentQuestion,
                setModalShow,
                surveyHeaders,
                toggleExpand,
            })}
            {search === "" && !selectedSurveyHeaderId && (
                <Paginations
                    perPage={perPage}
                    total={totalQuestions}
                    paginate={paginate}
                    currentPage={currentPage}
                />
            )}
            {modalShow.edit && (
                <QuestionForm
                    action="Edit"
                    question={currentQuestion}
                    setPopup={() => setModalShow({ ...modalShow, edit: false })}
                />
            )}
            {modalShow.delete && (
                <DeletePopup
                    question_id={currentQuestion?.id}
                    setPopup={() => setModalShow({ ...modalShow, delete: false })}
                    onDeleteSuccess={handleDeleteSuccess}
                />
            )}
        </main>
    );
}

function renderQuestionSections({
    canReorderQuestions,
    draggedQuestionId,
    expandedId,
    handleDragEnd,
    handleDragStart,
    handleDrop,
    isReordering,
    modalShow,
    questions,
    setCurrentQuestion,
    setModalShow,
    surveyHeaders,
    toggleExpand,
}) {
    const sectionGroups = groupQuestionsBySection(questions, surveyHeaders);

    if (sectionGroups.length === 0) {
        return (
            <div className="table-responsive">
                {renderQuestionsTable({
                    canReorderQuestions,
                    draggedQuestionId,
                    emptyMessage: 'No questions available',
                    expandedId,
                    handleDragEnd,
                    handleDragStart,
                    handleDrop,
                    isReordering,
                    modalShow,
                    questionsSubset: [],
                    setCurrentQuestion,
                    setModalShow,
                    surveyHeaders,
                    toggleExpand,
                })}
            </div>
        );
    }

    return sectionGroups.map((sectionGroup) => (
        <div className="mb-4" key={sectionGroup.sectionId}>
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-2">{sectionGroup.sectionName}</h2>
                <span className="text-muted">
                    {sectionGroup.questions.length} question{sectionGroup.questions.length === 1 ? '' : 's'}
                </span>
            </div>
            <div className="table-responsive">
                {renderQuestionsTable({
                    canReorderQuestions,
                    draggedQuestionId,
                    emptyMessage: 'No questions in this section',
                    expandedId,
                    handleDragEnd,
                    handleDragStart,
                    handleDrop,
                    isReordering,
                    modalShow,
                    questionsSubset: sectionGroup.questions,
                    setCurrentQuestion,
                    setModalShow,
                    surveyHeaders,
                    toggleExpand,
                })}
            </div>
        </div>
    ));
}

function renderQuestionsTable({
    canReorderQuestions,
    draggedQuestionId,
    emptyMessage,
    expandedId,
    handleDragEnd,
    handleDragStart,
    handleDrop,
    isReordering,
    modalShow,
    questionsSubset,
    setCurrentQuestion,
    setModalShow,
    surveyHeaders,
    toggleExpand,
}) {
    return (
        <table className="table table-hover">
            <thead className="thead-dark">
                <tr>
                    <th scope="col" style={{ width: '48px' }}>Move</th>
                    <th scope="col">Question Text</th>
                    <th scope="col">Type</th>
                    <th scope="col">Sort Order</th>
                    <th scope="col">Status</th>
                    <th scope="col">Details</th>
                    <th scope="col">Edit</th>
                    <th scope="col">Delete</th>
                </tr>
            </thead>
            <tbody>
                {questionsSubset.length > 0 ? questionsSubset.map((question) => (
                    <React.Fragment key={question.id}>
                        <tr
                            onClick={() => toggleExpand(question.id)}
                            style={{ cursor: 'pointer', opacity: draggedQuestionId === question.id ? 0.55 : 1 }}
                            draggable={canReorderQuestions && !isReordering}
                            onDragStart={() => handleDragStart(question.id)}
                            onDragEnd={handleDragEnd}
                            onDragOver={(event) => {
                                if (canReorderQuestions) {
                                    event.preventDefault();
                                }
                            }}
                            onDrop={() => handleDrop(question)}
                        >
                            <td
                                onClick={(event) => event.stopPropagation()}
                                style={{ cursor: canReorderQuestions ? 'grab' : 'not-allowed' }}
                            >
                                <FiMenu className={canReorderQuestions ? 'text-primary' : 'text-muted'} />
                            </td>
                            <td>{question.question_text}</td>
                            <td>{question.questionType}</td>
                            <td>{question.sort_order ?? 0}</td>
                            <td>{question.status ? 'Active' : 'Inactive'}</td>
                            <td>{expandedId === question.id ? <FiChevronUp /> : <FiChevronDown />}</td>
                            <td>
                                <FiEdit
                                    className="text-primary"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setCurrentQuestion(question);
                                        setModalShow({ ...modalShow, edit: true });
                                    }}
                                />
                            </td>
                            <td>
                                <FiTrash
                                    className="text-danger"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setCurrentQuestion(question);
                                        setModalShow({ ...modalShow, delete: true });
                                    }}
                                />
                            </td>
                        </tr>
                        {expandedId === question.id && (
                            <tr>
                                <td colSpan="8">
                                    <div>
                                        <p><strong>Subtext:</strong> {question.question_subtext || "None"}</p>
                                        <p><strong>Survey Header:</strong> {question.survey_name || question.surveyHeader?.survey_name || question.survey_header?.survey_name || surveyHeaders.find((header) => header.id === question.survey_header_id)?.survey_name || question.survey_header_id || "None"}</p>
                                        <p><strong>Survey Section ID:</strong> {question.survey_section_id || "None"}</p>
                                        <p><strong>Option Group:</strong> {question.optiongroupdetails?.name || "No group"}</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                )) : (
                    <tr>
                        <td colSpan="8">{emptyMessage}</td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}

function sortQuestions(questions) {
    return [...questions].sort((firstQuestion, secondQuestion) => {
        if ((firstQuestion.survey_section_id || 0) !== (secondQuestion.survey_section_id || 0)) {
            return (firstQuestion.survey_section_id || 0) - (secondQuestion.survey_section_id || 0);
        }

        if ((firstQuestion.sort_order || 0) !== (secondQuestion.sort_order || 0)) {
            return (firstQuestion.sort_order || 0) - (secondQuestion.sort_order || 0);
        }

        return firstQuestion.id - secondQuestion.id;
    });
}

function reorderQuestionsWithinSection(allQuestions, draggedQuestionId, targetQuestionId) {
    const draggedQuestion = allQuestions.find((question) => question.id === draggedQuestionId);

    if (!draggedQuestion) {
        return allQuestions;
    }

    const sectionQuestions = sortQuestions(
        allQuestions.filter((question) => question.survey_section_id === draggedQuestion.survey_section_id)
    );
    const otherQuestions = allQuestions.filter(
        (question) => question.survey_section_id !== draggedQuestion.survey_section_id
    );
    const draggedIndex = sectionQuestions.findIndex((question) => question.id === draggedQuestionId);
    const targetIndex = sectionQuestions.findIndex((question) => question.id === targetQuestionId);

    if (draggedIndex === -1 || targetIndex === -1) {
        return allQuestions;
    }

    const reorderedSectionQuestions = [...sectionQuestions];
    const [movedQuestion] = reorderedSectionQuestions.splice(draggedIndex, 1);
    reorderedSectionQuestions.splice(targetIndex, 0, movedQuestion);

    return sortQuestions([
        ...otherQuestions,
        ...reorderedSectionQuestions.map((question, index) => ({
            ...question,
            sort_order: index + 1,
        })),
    ]);
}

function groupQuestionsBySection(questions, surveyHeaders) {
    const groupedQuestions = sortQuestions(questions).reduce((groups, question) => {
        const key = question.survey_section_id || 'no-section';

        if (!groups[key]) {
            const matchingHeader = surveyHeaders.find((header) => header.id === question.survey_header_id);
            const matchingSection = matchingHeader?.sections?.find(
                (section) => section.id === question.survey_section_id
            );

            groups[key] = {
                sectionId: key,
                sectionName:
                    matchingSection?.section_title ||
                    matchingSection?.section_name ||
                    `Section ${question.survey_section_id || 'Unknown'}`,
                questions: [],
            };
        }

        groups[key].questions.push(question);
        return groups;
    }, {});

    return Object.values(groupedQuestions);
}

export default Questions;
